import React, { useEffect, useRef } from "react";

/**
 * HavnTalkWave — analog sine waveform overlay for HAVN's voice button area.
 *
 * Renders a horizontal glowing waveform that responds to TTS amplitude.
 * Designed to sit BELOW the existing cobalt orb on HAVN's homepage, between
 * the orb and the "Tap to talk" status label.
 *
 * Visual reference: glowing cyan-white core line on cobalt blue background,
 * with a softer secondary trail behind it. Frequency and amplitude scale
 * with state and live audio loudness.
 *
 * Props:
 *   state      'idle' | 'listening' | 'thinking' | 'speaking'
 *   amplitude  0..1, updated at 60fps by parent from audio source
 *   width      number, px (default 280)
 *   height     number, px (default 64)
 *   scrambled  bool, optional more chaotic waveform (default false)
 *   className  string, optional wrapper classes
 *
 * Performance:
 *   - The 60fps RAF loop writes the SVG path's `d` attribute directly to a
 *     DOM ref via setAttribute. React only re-renders when `state` or
 *     `scrambled` props change (a few times per session).
 *   - Smoothed amplitude lives in a useRef, NOT useState, so updating it
 *     does not trigger re-renders.
 *   - Time tracking is internal to the RAF loop, NOT a separate setState
 *     hook. This avoids the dual re-render storm GPT's original had.
 *
 * Accessibility:
 *   Respects prefers-reduced-motion — disables animation entirely and
 *   renders a static low-amplitude wave.
 */

export default function HavnTalkWave({
  state = "idle",
  amplitude = 0,
  width = 280,
  height = 64,
  scrambled = false,
  color = "#3FAFD1",
  className = "",
}) {
  // Refs for direct DOM writes (60fps hot path, no React involvement)
  const primaryPathRef = useRef(null);
  const secondaryPathRef = useRef(null);

  // Live values that don't trigger re-renders
  const amplitudeRef = useRef(0);
  const smoothedRef = useRef(0);
  const stateRef = useRef(state);
  const scrambledRef = useRef(scrambled);
  const frameRef = useRef(0);
  const lastTimeRef = useRef(0);
  const startTimeRef = useRef(0);
  const reducedMotionRef = useRef(false);

  // Mirror props into refs so the RAF loop reads latest without re-running
  amplitudeRef.current = Math.max(0, Math.min(1, amplitude));
  stateRef.current = state;
  scrambledRef.current = scrambled;

  // ─── Reduced motion detection ─────────────────────────────────────
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    reducedMotionRef.current = mq.matches;
    const handler = (e) => { reducedMotionRef.current = e.matches; };
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  // ─── 60fps animation loop ─────────────────────────────────────────
  useEffect(() => {
    const tick = (now) => {
      if (!startTimeRef.current) startTimeRef.current = now;
      if (!lastTimeRef.current) lastTimeRef.current = now;

      const dt = Math.min(64, now - lastTimeRef.current);
      lastTimeRef.current = now;
      const elapsed = now - startTimeRef.current;

      const currentState = stateRef.current;
      const currentScrambled = scrambledRef.current;
      const reduced = reducedMotionRef.current;

      // Asymmetric attack/release smoothing — the key to articulation feel
      const target = amplitudeRef.current;
      const rising = target > smoothedRef.current;
      const tau = rising ? 75 : 240;
      const factor = 1 - Math.exp(-dt / tau);
      smoothedRef.current += (target - smoothedRef.current) * factor;

      // Floor keeps the wave alive even during silence in active states
      const floor = currentState === "idle" || currentState === "thinking" ? 0.02 : 0.05;
      let a = Math.max(smoothedRef.current, floor);

      // Reduced motion: clamp amplitude response to 30% and freeze time
      const effectiveTime = reduced ? 0 : elapsed;
      if (reduced) a *= 0.3;

      // Build paths and write directly to DOM
      const primaryPath = buildWavePath({
        width,
        height,
        time: effectiveTime,
        amplitude: a,
        mode: currentState,
        scrambled: currentScrambled,
        phaseOffset: 0,
      });

      const secondaryPath = buildWavePath({
        width,
        height,
        time: effectiveTime,
        amplitude: Math.max(0.02, a * 0.8),
        mode: currentState,
        scrambled: currentScrambled,
        phaseOffset: 0.9,
      });

      if (primaryPathRef.current) {
        primaryPathRef.current.setAttribute("d", primaryPath);
      }
      if (secondaryPathRef.current) {
        secondaryPathRef.current.setAttribute("d", secondaryPath);
      }

      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      frameRef.current = 0;
      lastTimeRef.current = 0;
      startTimeRef.current = 0;
    };
    // Intentionally empty deps — the loop reads from refs and runs for component lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height]);

  // State-driven static colors (these only change on state transitions, OK to compute on render)
  const isActive = state !== "idle";
  const strokePrimary = hexToRgba(color, isActive ? 0.98 : 0.92);
  const strokeSecondary = hexToRgba(color, isActive ? 0.62 : 0.42);
  const glowStdDev = state === "speaking" ? 1.6 : state === "listening" ? 1.2 : 0.9;

  return (
    <div
      className={className}
      style={{
        width,
        height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
      }}
      aria-hidden="true"
    >
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{ overflow: "visible" }}
      >
        <defs>
          <filter id="havn-talkwave-glow" x="-50%" y="-200%" width="200%" height="500%">
            <feGaussianBlur stdDeviation={glowStdDev} result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <path
          ref={secondaryPathRef}
          d=""
          fill="none"
          stroke={strokeSecondary}
          strokeWidth={2}
          strokeLinecap="round"
          filter="url(#havn-talkwave-glow)"
          opacity={0.8}
        />
        <path
          ref={primaryPathRef}
          d=""
          fill="none"
          stroke={strokePrimary}
          strokeWidth={2.6}
          strokeLinecap="round"
          filter="url(#havn-talkwave-glow)"
        />
      </svg>
    </div>
  );
}

// ─── Wave path generation (pure function, called from RAF loop) ──────
// Generates an SVG path string for a tapered sine wave.
//
// The taper (sin(πn)^0.75) makes the wave fade to zero at both ends so it
// doesn't crash awkwardly into the container edges — this is the detail
// that makes it look professional vs amateur.
//
// In `scrambled` mode, three sine layers at different frequencies are
// combined to create a more complex "alive" waveform without going chaotic.
function buildWavePath({ width, height, time, amplitude, mode, scrambled, phaseOffset = 0 }) {
  const points = 44;
  const midY = height / 2;

  const usableAmp =
    mode === "idle"
      ? height * 0.07
      : mode === "listening"
        ? height * (0.10 + amplitude * 0.14)
        : mode === "thinking"
          ? height * 0.08
          : height * (0.14 + amplitude * 0.30); // speaking

  const freq =
    mode === "idle"
      ? 1.8
      : mode === "listening"
        ? 2.2
        : mode === "thinking"
          ? 2.0
          : scrambled
            ? 3.2
            : 2.6;

  const speed =
    mode === "idle"
      ? 0.0011
      : mode === "listening"
        ? 0.0018
        : mode === "thinking"
          ? 0.0014
          : 0.0026;

  const phase = time * speed + phaseOffset;

  const pts = [];
  for (let i = 0; i <= points; i += 1) {
    const x = (i / points) * width;
    const n = i / points;

    let y = midY + Math.sin(n * Math.PI * 2 * freq + phase) * usableAmp;

    // Tapered envelope — fade to zero at both ends
    const taper = Math.sin(Math.PI * n) ** 0.75;
    y = midY + (y - midY) * taper;

    // Scrambled mode: layer additional harmonics for complexity
    if (scrambled && mode !== "idle") {
      const y2 = Math.sin(n * Math.PI * 2 * (freq * 1.9) - phase * 1.3) * usableAmp * 0.32;
      const y3 = Math.sin(n * Math.PI * 2 * (freq * 0.7) + phase * 0.6) * usableAmp * 0.18;
      y += (y2 + y3) * taper;
    }

    pts.push({ x, y });
  }

  return toSmoothPath(pts);
}

// Quadratic Bézier smoothing for the path — gives the flowing analog look
// instead of jagged segment-to-segment transitions.
function toSmoothPath(points) {
  if (!points.length) return "";
  let d = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;

  for (let i = 1; i < points.length; i += 1) {
    const p0 = points[i - 1];
    const p1 = points[i];
    const cx = ((p0.x + p1.x) / 2).toFixed(2);
    d += ` Q ${cx} ${p0.y.toFixed(2)}, ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`;
  }

  return d;
}

// Convert a hex color string (#RRGGBB) to an rgba() CSS value.
// Used by the component to derive theme-aware stroke colors from
// the `color` prop at runtime.
function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
